import {createReducer, on} from "@ngrx/store";
import {INITIAL_NOTIFICATION_STATE, NotificationState} from "./notification.state";
import {
    NotificationActions,
    NotificationCountsActions,
    NotificationListActions,
    SubscriptionActions,
} from "./notification.actions";

export const notificationReducer = createReducer<NotificationState>(
    INITIAL_NOTIFICATION_STATE,

    // ---------- counts ----------
    on(NotificationCountsActions.load, (state) => ({
        ...state,
        counts: {...state.counts, status: "LOADING", errorMessage: ""},
    })),
    on(NotificationCountsActions.loadSuccess, (state, {counts}) => ({
        ...state,
        counts: {data: counts, status: "LOADED", errorMessage: ""},
        pollFailureCount: 0,
    })),
    on(NotificationCountsActions.loadError, (state, {errorMsg}) => ({
        ...state,
        counts: {...state.counts, status: "ERROR", errorMessage: errorMsg},
        pollFailureCount: state.pollFailureCount + 1,
    })),
    on(NotificationCountsActions.reset, () => INITIAL_NOTIFICATION_STATE),

    // ---------- list ----------
    on(NotificationListActions.load, (state) => ({
        ...state,
        list: {...state.list, status: "LOADING", errorMessage: ""},
    })),
    on(NotificationListActions.loadSuccess, (state, {items}) => ({
        ...state,
        list: {data: items, status: "LOADED", errorMessage: ""},
    })),
    on(NotificationListActions.loadError, (state, {errorMsg}) => ({
        ...state,
        list: {...state.list, status: "ERROR", errorMessage: errorMsg},
    })),

    // Panel opens unread-only; resets the showRead toggle every time so the user always
    // starts on the unread view. Panel close also resets so the next open is clean.
    on(NotificationListActions.panelOpened, (state) => ({...state, showRead: false})),
    on(NotificationListActions.panelClosed, (state) => ({...state, showRead: false})),
    on(NotificationListActions.showRead, (state) => ({...state, showRead: true})),
    on(NotificationListActions.hideRead, (state) => ({...state, showRead: false})),

    // ---------- mark read / mark all read / dismiss (optimistic) ----------
    on(NotificationActions.markRead, (state, {id}) => ({
        ...state,
        counts: applyUnreadDelta(state, -1),
        list: {
            ...state.list,
            data: state.list.data.map((n) =>
                n.id === id && !n.readAt ? {...n, readAt: nowIso()} : n
            ),
        },
    })),
    on(NotificationActions.markReadError, (state, {id}) => ({
        // rollback the optimistic update
        ...state,
        counts: applyUnreadDelta(state, +1),
        list: {
            ...state.list,
            data: state.list.data.map((n) =>
                n.id === id ? {...n, readAt: undefined} : n
            ),
        },
    })),

    on(NotificationActions.markAllRead, (state) => ({
        ...state,
        counts: {...state.counts, data: {...state.counts.data, unread: 0}},
        list: {
            ...state.list,
            data: state.list.data.map((n) => (n.readAt ? n : {...n, readAt: nowIso()})),
        },
    })),
    on(NotificationActions.markAllReadError, (state, {errorMsg}) => ({
        // we don't perfectly roll back here — next counts poll repairs it
        ...state,
        list: {...state.list, errorMessage: errorMsg},
    })),

    // Optimistic prefix-restricted bulk mark-read. Match logic mirrors the backend:
    // exact equality OR proper sub-path with '/' separator (so prefix "/import"
    // sweeps "/import" and "/import/scraper/123" but NOT "/important-thing").
    on(NotificationActions.markReadByDeepLinkPrefix, (state, {deepLinkPrefix}) => {
        const matchesPrefix = (n: {deepLink?: string}) =>
            n.deepLink !== undefined &&
            (n.deepLink === deepLinkPrefix || n.deepLink.startsWith(deepLinkPrefix + "/"));
        const newlyReadCount = state.list.data.filter((n) => !n.readAt && matchesPrefix(n)).length;
        return {
            ...state,
            counts: {
                ...state.counts,
                data: {
                    ...state.counts.data,
                    unread: Math.max(0, state.counts.data.unread - newlyReadCount),
                },
            },
            list: {
                ...state.list,
                data: state.list.data.map((n) =>
                    !n.readAt && matchesPrefix(n) ? {...n, readAt: nowIso()} : n
                ),
            },
        };
    }),
    on(NotificationActions.markReadByDeepLinkPrefixError, (state, {errorMsg}) => ({
        // no perfect rollback — counts repair on the next poll. We don't surface a snackbar
        // for this background sweep; just record the message so the panel can show it if it
        // happens to be open.
        ...state,
        list: {...state.list, errorMessage: errorMsg},
    })),

    on(NotificationActions.dismiss, (state, {id}) => {
        const dismissed = state.list.data.find((n) => n.id === id);
        const wasUnread = dismissed && !dismissed.readAt;
        return {
            ...state,
            counts: {
                ...state.counts,
                data: {
                    unread: Math.max(0, state.counts.data.unread - (wasUnread ? 1 : 0)),
                    total: Math.max(0, state.counts.data.total - 1),
                },
            },
            list: {
                ...state.list,
                data: state.list.data.filter((n) => n.id !== id),
            },
        };
    }),
    on(NotificationActions.dismissError, (state, {errorMsg}) => ({
        // counts repair on next poll; surface the message
        ...state,
        list: {...state.list, errorMessage: errorMsg},
    })),

    // ---------- tracked jobs (drives polling cadence) ----------
    on(NotificationActions.trackJob, (state, {taskInfoId}) =>
        state.trackedJobIds.includes(taskInfoId)
            ? state
            : {...state, trackedJobIds: [...state.trackedJobIds, taskInfoId]}
    ),
    on(NotificationActions.untrackJob, (state, {taskInfoId}) => ({
        ...state,
        trackedJobIds: state.trackedJobIds.filter((id) => id !== taskInfoId),
    })),

    // ---------- subscriptions ----------
    on(SubscriptionActions.load, (state) => ({
        ...state,
        subscriptions: {...state.subscriptions, status: "LOADING", errorMessage: ""},
    })),
    on(SubscriptionActions.loadSuccess, (state, {subscriptions}) => ({
        ...state,
        subscriptions: {data: subscriptions, status: "LOADED", errorMessage: ""},
    })),
    on(SubscriptionActions.loadError, (state, {errorMsg}) => ({
        ...state,
        subscriptions: {...state.subscriptions, status: "ERROR", errorMessage: errorMsg},
    })),

    // optimistic subscribe — placeholder row; replaced on success
    on(SubscriptionActions.subscribe, (state, {targetType, targetId}) => {
        if (state.subscriptions.data.some((s) => s.targetType === targetType && s.targetId === targetId)) {
            return state;
        }
        return {
            ...state,
            subscriptions: {
                ...state.subscriptions,
                data: [
                    ...state.subscriptions.data,
                    {id: -1, targetType, targetId, subscribedAt: nowIso()},
                ],
            },
        };
    }),
    on(SubscriptionActions.subscribeSuccess, (state, {targetType, targetId, subscription}) => ({
        ...state,
        subscriptions: {
            ...state.subscriptions,
            data: state.subscriptions.data.map((s) =>
                s.targetType === targetType && s.targetId === targetId ? subscription : s
            ),
        },
    })),
    on(SubscriptionActions.subscribeError, (state, {targetType, targetId, errorMsg}) => ({
        // rollback the optimistic insertion
        ...state,
        subscriptions: {
            ...state.subscriptions,
            data: state.subscriptions.data.filter(
                (s) => !(s.targetType === targetType && s.targetId === targetId && s.id === -1)
            ),
            errorMessage: errorMsg,
        },
    })),

    on(SubscriptionActions.unsubscribe, (state, {targetType, targetId}) => ({
        ...state,
        subscriptions: {
            ...state.subscriptions,
            data: state.subscriptions.data.filter(
                (s) => !(s.targetType === targetType && s.targetId === targetId)
            ),
        },
    })),
    on(SubscriptionActions.unsubscribeError, (state, {errorMsg}) => ({
        // we won't perfectly restore the row (lost the original id); next subscriptionsLoad repairs it
        ...state,
        subscriptions: {...state.subscriptions, errorMessage: errorMsg},
    })),
);

function applyUnreadDelta(state: NotificationState, delta: number) {
    return {
        ...state.counts,
        data: {
            ...state.counts.data,
            unread: Math.max(0, state.counts.data.unread + delta),
        },
    };
}

function nowIso(): string {
    return new Date().toISOString();
}
